import React from 'react'
import { useTranslations } from '~/components/Locale'
import type { ArrayElement, Product, SimpleVariant, Variant } from '~/types'
import { cn, extractUpsells, toGqlProduct, UpsellInfo, useSelector } from '~/utils'
import { Upsell, UpsellProps } from './Upsell'

interface Props {
	product: Product
	upsellIds?: Array<Variant['id']>
	selectedVariant: ArrayElement<Product['variants']>
	disabled?: boolean
	offerTexts?: Array<UpsellInfo['label']>
	expirations?: Array<UpsellInfo['expiration']>
	/**
	 * Override default add to cart action
	 */
	onAdd?: (v: SimpleVariant & { discounts?: number }) => void
	onRemove?: (v: SimpleVariant) => void
}
export type UpsellsProps = Omit<Props, 'product' | 'selectedVariant'> & {
	variants: Array<UpsellProps['variant']>
	mockCart?: UpsellProps['mockCart']
}

export const UpsellComponent: React.FC<UpsellsProps> = ({ variants, disabled, offerTexts, expirations, ...props }) => {
	const t = useTranslations('react')
	return (
		<div className={cn({ 'opacity-step pointer-events-none': disabled })}>
			<h3 className="m-0 text-xl font-semibold">{t?.auk_page.choose_accessories}</h3>
			<ul className="list space-y-6">
				{variants.map((variant, i) => {
					const offer = offerTexts?.[i]
					const expiration = expirations?.[i] ?? null
					return (
						<Upsell
							className="w-full"
							key={variant.id}
							variant={variant}
							label={offer}
							expiration={expiration}
							mode="product"
							{...props}
						/>
					)
				})}
			</ul>
		</div>
	)
}

const Upsells: React.FC<Props> = ({ product, selectedVariant, ...props }) => {
	const { upsellCart, upsells } = useSelector(s => s.products)
	const upsellInfo = extractUpsells(product, selectedVariant)
	return upsells ? (
		<UpsellComponent
			variants={upsells}
			mockCart={upsellCart}
			offerTexts={upsellInfo.map(u => u.label)}
			expirations={upsellInfo.map(u => u.expiration)}
			{...props}
		/>
	) : (
		<UpsellComponent
			offerTexts={upsellInfo.map(u => u.label)}
			expirations={upsellInfo.map(u => u.expiration)}
			variants={
				window.UPSELLS
					? window.UPSELLS.map(toGqlProduct).map(p => ({
							...p.variants[0],
							price: p.priceRange.minVariantPrice,
							product: p
					  }))
					: []
			}
			{...props}
		/>
	)
}

const UpsellsWrapper: React.FC<Props> = ({ product, selectedVariant, ...props }) => {
	const upsells = extractUpsells(product, selectedVariant)
	if (upsells.length == 0) return null
	return <Upsells product={product} selectedVariant={selectedVariant} {...props} />
}

export { UpsellsWrapper as Upsells }
